<template>
  <div class="ingreso-store-tab">
    <div class="panel-toolbar mb-3">
      <p class="panel-toolbar-meta mb-0">
        Productos de <strong>ingreso.kameseeds.com</strong> — carrito, envío y pedidos.
      </p>
      <div class="panel-toolbar-actions d-flex flex-wrap gap-2 align-items-center">
        <ProductViewToggle
          :model-value="viewMode"
          class="d-none d-md-inline-flex"
          @update:model-value="$emit('update:viewMode', $event)"
        />
        <button class="btn btn-outline-primary" @click="openImportModal">
          <i class="fas fa-file-import me-2"></i>Importar del catálogo web
        </button>
        <button class="btn btn-primary" @click="openModal()">
          <i class="fas fa-plus me-2"></i>Producto exclusivo
        </button>
      </div>
    </div>

    <div class="info-banner panel-card mb-4">
      <i class="fas fa-ruler-combined me-2"></i>
      Correo Argentino: peso en <strong>gramos</strong>, dimensiones en <strong>cm</strong>.
      Si faltan medidas, se usan los
      <router-link to="/panel/envios/configuracion" class="alert-link">valores por defecto de envíos</router-link>.
    </div>

    <ProductSelectionBar
      v-if="filteredItems.length"
      input-id="store-select-all"
      :selected-count="selectedCount"
      :total-count="filteredItems.length"
      @toggle-all="toggleAllVisible"
      @clear="clear"
    >
      <template #actions>
        <button type="button" class="btn btn-sm btn-success" :disabled="bulkWorking" @click="requestBulkActive(true)">
          Activar
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="bulkWorking" @click="requestBulkActive(false)">
          Desactivar
        </button>
        <button type="button" class="btn btn-sm btn-danger" :disabled="bulkWorking" @click="requestBulkDelete">
          Quitar de tienda
        </button>
      </template>
    </ProductSelectionBar>

    <div class="row mb-3 g-2">
      <div class="col-md-6">
        <input v-model="search" type="text" class="form-control" placeholder="Buscar por nombre..." />
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
    </div>

    <!-- Cards -->
    <div v-else-if="viewMode === 'cards'" class="row g-4">
      <div v-for="item in filteredItems" :key="item.id" class="col-md-6 col-lg-4">
        <div class="card product-card bg-dark text-light h-100" :class="{ 'product-card--selected': isSelected(item.id) }">
          <div class="card-body d-flex flex-column">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :checked="isSelected(item.id)"
                  @change="toggle(item.id)"
                />
              </div>
              <span class="badge" :class="item.source === 'custom' ? 'bg-warning text-dark' : 'bg-info text-dark'">
                {{ item.source === 'custom' ? 'Exclusivo' : 'Importado' }}
              </span>
            </div>

            <div class="product-thumb-wrap mb-3">
              <ProductImage :product="item" :sources="item.product_detail?.images?.length ? item.product_detail.images : [item.product_image, item.custom_image]" :alt="item.product_name" class="product-thumb" />
            </div>

            <h5 class="fs-6 fw-bold mb-1">{{ item.product_name }}</h5>
            <p v-if="item.product_genotype" class="text-muted small mb-2">{{ item.product_genotype }}</p>

            <div class="d-flex flex-wrap gap-1 mb-2">
              <span class="badge bg-secondary">x{{ item.seed_count }}</span>
              <span class="badge" :class="item.is_active ? 'bg-success' : 'bg-secondary'">
                {{ item.is_active ? 'Activo' : 'Inactivo' }}
              </span>
              <span v-if="item.in_public_catalog" class="badge bg-primary">Catálogo web</span>
            </div>

            <div class="text-success fw-semibold mb-1">{{ formatPrice(item.final_price) }}</div>
            <div class="text-muted small mb-3">
              {{ item.weight_grams }}g · {{ item.length_cm }}×{{ item.width_cm }}×{{ item.height_cm }} cm
            </div>

            <div class="d-flex gap-2 mt-auto">
              <router-link
                v-if="item.source === 'catalog' && item.product_id"
                :to="`/panel/productos/editar/${item.product_id}`"
                class="btn btn-sm btn-outline-primary flex-grow-1"
              >
                <i class="fas fa-edit me-1"></i>Editar ficha
              </router-link>
              <button class="btn btn-sm btn-secondary flex-grow-1" @click="openModal(item)">
                <i class="fas fa-pen me-1"></i>Tienda
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="requestSingleDelete(item)">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!filteredItems.length" class="col-12 text-center text-muted py-5">
        No hay productos en la tienda ingreso.
      </div>
    </div>

    <!-- List -->
    <div v-else class="table-responsive panel-table-wrap">
      <table class="table table-dark table-hover align-middle mb-0">
        <thead>
          <tr>
            <th style="width: 40px;">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="filteredItems.length && selectedCount === filteredItems.length"
                :indeterminate="selectedCount > 0 && selectedCount < filteredItems.length"
                @change="toggleAllVisible"
              />
            </th>
            <th style="width: 72px;"></th>
            <th>Producto</th>
            <th>Origen</th>
            <th>Presentación</th>
            <th>Precio</th>
            <th>Peso / dim.</th>
            <th>Estado</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in filteredItems" :key="item.id" :class="{ 'table-active': isSelected(item.id) }">
            <td>
              <input class="form-check-input" type="checkbox" :checked="isSelected(item.id)" @change="toggle(item.id)" />
            </td>
            <td>
              <ProductImage :product="item" :sources="item.product_detail?.images?.length ? item.product_detail.images : [item.product_image, item.custom_image]" :alt="item.product_name" class="list-thumb" />
            </td>
            <td>
              <div class="fw-medium">{{ item.product_name }}</div>
              <small v-if="item.product_genotype" class="text-muted d-block">{{ item.product_genotype }}</small>
              <small v-if="item.in_public_catalog" class="text-muted">Del catálogo web</small>
              <small v-else-if="item.source === 'custom'" class="text-warning">Solo tienda ingreso</small>
            </td>
            <td>
              <span class="badge" :class="item.source === 'custom' ? 'bg-warning text-dark' : 'bg-info text-dark'">
                {{ item.source === 'custom' ? 'Exclusivo' : 'Importado' }}
              </span>
            </td>
            <td>x{{ item.seed_count }}</td>
            <td>{{ formatPrice(item.final_price) }}</td>
            <td class="small text-muted">
              {{ item.weight_grams }}g · {{ item.length_cm }}×{{ item.width_cm }}×{{ item.height_cm }}
            </td>
            <td>
              <span class="badge" :class="item.is_active ? 'bg-success' : 'bg-secondary'">
                {{ item.is_active ? 'Activo' : 'Inactivo' }}
              </span>
            </td>
            <td class="text-end text-nowrap">
              <router-link
                v-if="item.source === 'catalog' && item.product_id"
                :to="`/panel/productos/editar/${item.product_id}`"
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar ficha del catálogo web"
              >
                <i class="fas fa-edit"></i>
              </router-link>
              <button class="btn btn-sm btn-outline-light me-1" title="Tienda y envío" @click="openModal(item)">
                <i class="fas fa-pen"></i>
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="requestSingleDelete(item)">
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
          <tr v-if="!filteredItems.length">
            <td colspan="9" class="text-center text-muted py-4">
              No hay productos en la tienda ingreso. Importá del catálogo web o creá uno exclusivo.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal crear/editar -->
    <div v-if="showModal" class="panel-modal-overlay" @click.self="showModal = false">
      <div class="panel-modal modal-lg">
        <h3>{{ editing ? 'Editar ítem' : (formMode === 'custom' ? 'Producto exclusivo' : 'Publicar en tienda ingreso') }}</h3>

        <div v-if="!editing" class="btn-group mb-3 w-100">
          <button
            type="button"
            class="btn"
            :class="formMode === 'catalog' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="formMode = 'catalog'"
          >
            Desde catálogo web
          </button>
          <button
            type="button"
            class="btn"
            :class="formMode === 'custom' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="formMode = 'custom'"
          >
            Solo tienda ingreso
          </button>
        </div>

        <form @submit.prevent="save">
          <div class="row g-3">
            <template v-if="showCatalogPicker">
              <div class="col-md-6">
                <label class="form-label">Producto (catálogo web)</label>
                <select v-model="form.product" class="form-select" required @change="onProductChange">
                  <option :value="null" disabled>Elegir producto</option>
                  <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Presentación</label>
                <select v-model="form.blister" class="form-select" required :disabled="!form.product">
                  <option :value="null" disabled>Elegir presentación</option>
                  <option v-for="b in blistersForProduct" :key="b.id" :value="b.id">x{{ b.seed_count }}</option>
                </select>
              </div>
            </template>

            <template v-else-if="showCatalogEdit">
              <div class="col-12">
                <div class="catalog-edit-summary panel-card p-3">
                  <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div class="d-flex gap-3">
                      <ProductImage v-if="editing" :product="editing" :sources="editing.product_detail?.images?.length ? editing.product_detail.images : [editing.product_image, editing.custom_image]" alt="" class="modal-thumb" />
                      <div>
                        <div class="fw-semibold">{{ editingProductName }}</div>
                        <div class="text-muted small">Presentación x{{ editingSeedCount }}</div>
                        <div class="text-muted small mt-1">Nombre, precio y stock se editan en la ficha del catálogo web.</div>
                      </div>
                    </div>
                    <router-link
                      v-if="form.product"
                      :to="`/panel/productos/editar/${form.product}`"
                      class="btn btn-sm btn-outline-primary"
                      @click="showModal = false"
                    >
                      <i class="fas fa-edit me-1"></i>Editar en catálogo
                    </router-link>
                  </div>
                </div>
              </div>
            </template>

            <template v-else-if="showCustomFields">
              <div class="col-md-8">
                <label class="form-label">Nombre</label>
                <input v-model="form.custom_name" class="form-control" required maxlength="200" />
              </div>
              <div class="col-md-4">
                <label class="form-label">Precio (ARS)</label>
                <input v-model.number="form.custom_price" type="number" min="0" step="0.01" class="form-control" required />
              </div>
              <div class="col-md-4">
                <label class="form-label">Semillas / unidades</label>
                <input v-model.number="form.custom_seed_count" type="number" min="1" class="form-control" required />
              </div>
              <div class="col-md-4">
                <label class="form-label">Stock</label>
                <input v-model.number="form.custom_stock" type="number" min="0" class="form-control" required />
              </div>
              <div class="col-md-4">
                <label class="form-label">Imagen URL (opcional)</label>
                <input v-model="form.custom_image" class="form-control" placeholder="https://..." />
              </div>
            </template>

            <div class="col-12" v-if="editing || showCatalogPicker || showCustomFields">
              <hr class="border-secondary my-1" />
              <p class="text-muted small mb-0">
                <i class="fas fa-truck me-1"></i>Envío y visibilidad en la tienda ingreso
              </p>
            </div>

            <div class="col-md-3">
              <label class="form-label">Peso (g)</label>
              <input v-model.number="form.weight_grams" type="number" min="1" max="25000" class="form-control" required />
            </div>
            <div class="col-md-3">
              <label class="form-label">Largo (cm)</label>
              <input v-model.number="form.length_cm" type="number" min="1" max="150" class="form-control" required />
            </div>
            <div class="col-md-3">
              <label class="form-label">Ancho (cm)</label>
              <input v-model.number="form.width_cm" type="number" min="1" max="150" class="form-control" required />
            </div>
            <div class="col-md-3">
              <label class="form-label">Alto (cm)</label>
              <input v-model.number="form.height_cm" type="number" min="1" max="150" class="form-control" required />
            </div>
            <div class="col-md-4">
              <label class="form-label">Orden</label>
              <input v-model.number="form.sort_order" type="number" min="0" class="form-control" />
            </div>
            <div class="col-md-8 d-flex align-items-end">
              <div class="form-check">
                <input v-model="form.is_active" class="form-check-input" type="checkbox" id="tiendaActive" />
                <label class="form-check-label" for="tiendaActive">Visible en la tienda</label>
              </div>
            </div>
          </div>
          <div class="panel-modal-actions">
            <button type="button" class="btn btn-secondary" @click="showModal = false">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Guardando...' : 'Guardar' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal importar -->
    <div v-if="showImport" class="panel-modal-overlay" @click.self="showImport = false">
      <div class="panel-modal modal-lg">
        <h3>Importar del catálogo web</h3>
        <p class="text-muted small">
          Elegí presentaciones del catálogo web. Las ya publicadas en la tienda ingreso aparecen deshabilitadas.
        </p>
        <div class="row g-2 mb-3">
          <div class="col-md-3">
            <input v-model.number="importDefaults.weight_grams" type="number" class="form-control form-control-sm" placeholder="Peso g" />
          </div>
          <div class="col-md-3">
            <input v-model.number="importDefaults.length_cm" type="number" class="form-control form-control-sm" placeholder="L cm" />
          </div>
          <div class="col-md-3">
            <input v-model.number="importDefaults.width_cm" type="number" class="form-control form-control-sm" placeholder="A cm" />
          </div>
          <div class="col-md-3">
            <input v-model.number="importDefaults.height_cm" type="number" class="form-control form-control-sm" placeholder="H cm" />
          </div>
        </div>
        <div class="import-list panel-card p-2 mb-3" style="max-height: 360px; overflow-y: auto;">
          <div v-for="row in importRows" :key="row.key" class="form-check py-1 border-bottom border-secondary-subtle d-flex align-items-center gap-2">
            <input
              :id="row.key"
              v-model="selectedImport"
              class="form-check-input"
              type="checkbox"
              :value="row.key"
              :disabled="row.alreadyInStore"
            />
            <label :for="row.key" class="form-check-label w-100 d-flex align-items-center gap-2" :class="{ 'text-muted': row.alreadyInStore }">
              <img v-if="row.image" :src="row.image" alt="" class="import-thumb" />
              <span>{{ row.productName }} · x{{ row.seedCount }}</span>
              <span v-if="row.alreadyInStore" class="badge bg-secondary ms-auto">En tienda</span>
            </label>
          </div>
          <p v-if="!importRows.length" class="text-muted text-center py-3 mb-0">No hay productos en el catálogo web</p>
        </div>
        <div class="panel-modal-actions">
          <button type="button" class="btn btn-secondary" @click="showImport = false">Cancelar</button>
          <button type="button" class="btn btn-primary" :disabled="importing || !selectedImport.length" @click="runImport">
            {{ importing ? 'Importando...' : `Importar (${selectedImport.length})` }}
          </button>
        </div>
      </div>
    </div>

    <BulkActionConfirmModal
      :show="confirmModal.show"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :detail="confirmModal.detail"
      :warning="confirmModal.warning"
      :confirm-label="confirmModal.confirmLabel"
      :confirm-class="confirmModal.confirmClass"
      :loading="confirmModal.loading"
      @confirm="runConfirm"
      @cancel="closeConfirm"
    />
  </div>
</template>

<script setup>
import ProductImage from '@/components/ProductImage.vue'
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { getAllProductsForAdmin, onProductsChanged } from '@/services/products'
import { apiErrorMessage } from '@/utils/apiErrors'
import {
  getIngresoCatalog,
  createIngresoCatalogItem,
  updateIngresoCatalogItem,
  deleteIngresoCatalogItem,
  bulkImportIngresoCatalog,
} from '@/services/ingreso'
import { getShippingAdminConfig } from '@/services/shipping'
import { useItemSelection } from '@/composables/useItemSelection'
import { useUnsavedChangesGuard, useFormDirty } from '@/composables/useUnsavedChangesGuard'
import ProductViewToggle from '@/components/panel/ProductViewToggle.vue'
import ProductSelectionBar from '@/components/panel/ProductSelectionBar.vue'
import BulkActionConfirmModal from '@/components/panel/BulkActionConfirmModal.vue'

defineProps({
  viewMode: { type: String, default: 'cards' },
})

const emit = defineEmits(['update:viewMode', 'store-changed'])

const toast = useToast()
const items = ref([])
const products = ref([])
const loading = ref(true)
const saving = ref(false)
const importing = ref(false)
const bulkWorking = ref(false)
const showModal = ref(false)
const showImport = ref(false)
const editing = ref(null)
const formMode = ref('catalog')
const selectedImport = ref([])
const search = ref('')
const itemToDelete = ref(null)

const confirmModal = ref({
  show: false,
  title: '',
  message: '',
  detail: '',
  warning: '',
  confirmLabel: 'Confirmar',
  confirmClass: 'btn-primary',
  loading: false,
  handler: null,
})

const { selectedIds, selectedCount, isSelected, toggle, clear, toggleAll, pruneMissing } = useItemSelection()

const importDefaults = ref({ weight_grams: 500, length_cm: 20, width_cm: 15, height_cm: 10 })

const emptyForm = () => ({
  source: 'catalog',
  product: null,
  blister: null,
  custom_name: '',
  custom_image: '',
  custom_price: null,
  custom_seed_count: 1,
  custom_stock: 0,
  weight_grams: 500,
  length_cm: 20,
  width_cm: 15,
  height_cm: 10,
  sort_order: 0,
  is_active: true,
})

const form = ref(emptyForm())

const formDirty = useFormDirty(form)
const importDefaultsDirty = useFormDirty(importDefaults, {
  initial: { weight_grams: 500, length_cm: 20, width_cm: 15, height_cm: 10 },
})

// Cambios sin guardar: formulario de ítem abierto o selección/datos del importador.
const dirty = computed(() =>
  (showModal.value && formDirty.dirty.value) ||
  (showImport.value && (selectedImport.value.length > 0 || importDefaultsDirty.dirty.value))
)

const savePendingChanges = async () => {
  if (showModal.value) await save()
  else if (showImport.value) await runImport()
}

useUnsavedChangesGuard({
  dirty,
  saveFn: savePendingChanges,
  message: 'El formulario de la tienda ingreso tiene cambios sin guardar. Si salís ahora, se van a perder.',
})

const formatPrice = (v) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(v || 0)

const filteredItems = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return items.value
  return items.value.filter((item) => {
    const name = (item.product_name || '').toLowerCase()
    const genotype = (item.product_genotype || '').toLowerCase()
    return name.includes(q) || genotype.includes(q)
  })
})

watch(filteredItems, (list) => {
  pruneMissing(list.map((i) => i.id))
})

const toggleAllVisible = () => toggleAll(filteredItems.value.map((i) => i.id))

const existingKeys = computed(() => {
  const set = new Set()
  items.value.filter((i) => i.source === 'catalog').forEach((i) => {
    if (i.product_id && i.blister_id) set.add(`${i.product_id}-${i.blister_id}`)
  })
  return set
})

const importRows = computed(() => {
  const rows = []
  for (const p of products.value) {
    for (const b of p.blisters || []) {
      const key = `${p.id}-${b.id}`
      rows.push({
        key,
        product: p.id,
        blister: b.id,
        productName: p.name,
        seedCount: b.seed_count,
        image: p.image,
        alreadyInStore: existingKeys.value.has(key),
      })
    }
  }
  return rows.sort((a, b) => a.productName.localeCompare(b.productName))
})

const blistersForProduct = computed(() => {
  const p = products.value.find((x) => x.id === form.value.product)
  return p?.blisters || []
})

const showCatalogPicker = computed(() => !editing.value && formMode.value === 'catalog')
const showCatalogEdit = computed(() => editing.value?.source === 'catalog')
const showCustomFields = computed(
  () => (!editing.value && formMode.value === 'custom') || editing.value?.source === 'custom'
)

const editingProductName = computed(() => {
  if (!editing.value) return ''
  return products.value.find((p) => p.id === form.value.product)?.name || editing.value.product_name || 'Producto'
})

const editingSeedCount = computed(() => {
  if (!editing.value) return ''
  const b = blistersForProduct.value.find((x) => x.id === form.value.blister)
  return b?.seed_count ?? editing.value.seed_count ?? '—'
})

const notifyStoreChanged = () => emit('store-changed')

const openConfirm = (config) => {
  confirmModal.value = {
    show: true,
    title: '',
    message: '',
    detail: '',
    warning: '',
    confirmLabel: 'Confirmar',
    confirmClass: 'btn-primary',
    loading: false,
    handler: null,
    ...config,
  }
}

const closeConfirm = () => {
  if (confirmModal.value.loading) return
  confirmModal.value.show = false
  itemToDelete.value = null
}

const runConfirm = async () => {
  if (!confirmModal.value.handler) return
  confirmModal.value.loading = true
  try {
    await confirmModal.value.handler()
    confirmModal.value.show = false
    itemToDelete.value = null
  } catch (err) {
    toast.error(err.message || 'Error al ejecutar la acción')
  } finally {
    confirmModal.value.loading = false
  }
}

let itemsSeq = 0
let productsSeq = 0
const loadItems = async () => {
  const seq = ++itemsSeq
  const data = await getIngresoCatalog({ page_size: 500 })
  if (seq !== itemsSeq) return
  items.value = data.results || data
  notifyStoreChanged()
}

const loadProducts = async () => {
  const seq = ++productsSeq
  const all = []
  let page = 1
  let hasNext = true
  while (hasNext) {
    const data = await getAllProductsForAdmin(true, page, 100)
    if (seq !== productsSeq) return
    all.push(...data.results)
    hasNext = Boolean(data.next && data.results.length)
    page += 1
  }
  products.value = all
}

const applyProductShippingDims = (productId) => {
  const p = products.value.find((x) => x.id === productId)
  if (!p) return
  if (p.weight_grams) form.value.weight_grams = p.weight_grams
  if (p.length_cm) form.value.length_cm = p.length_cm
  if (p.width_cm) form.value.width_cm = p.width_cm
  if (p.height_cm) form.value.height_cm = p.height_cm
}

const onProductChange = () => {
  form.value.blister = null
  if (form.value.product) applyProductShippingDims(form.value.product)
}

const openModal = (item = null) => {
  editing.value = item
  if (item) {
    formMode.value = item.source === 'custom' ? 'custom' : 'catalog'
    form.value = {
      source: item.source,
      product: item.product_id,
      blister: item.blister_id,
      custom_name: item.custom_name || '',
      custom_image: item.custom_image || '',
      custom_price: item.custom_price,
      custom_seed_count: item.custom_seed_count || 1,
      custom_stock: item.custom_stock || 0,
      weight_grams: item.weight_grams,
      length_cm: item.length_cm,
      width_cm: item.width_cm,
      height_cm: item.height_cm,
      sort_order: item.sort_order,
      is_active: item.is_active,
    }
  } else {
    formMode.value = 'catalog'
    form.value = emptyForm()
  }
  formDirty.markClean()
  showModal.value = true
}

const openImportModal = () => {
  selectedImport.value = []
  showImport.value = true
}

const save = async () => {
  saving.value = true
  try {
    const isCustom = editing.value?.source === 'custom' || (!editing.value && formMode.value === 'custom')

    const payload = {
      weight_grams: form.value.weight_grams,
      length_cm: form.value.length_cm,
      width_cm: form.value.width_cm,
      height_cm: form.value.height_cm,
      sort_order: form.value.sort_order,
      is_active: form.value.is_active,
    }

    if (isCustom) {
      payload.source = 'custom'
      Object.assign(payload, {
        custom_name: form.value.custom_name?.trim(),
        custom_image: form.value.custom_image || null,
        custom_price: form.value.custom_price,
        custom_seed_count: form.value.custom_seed_count,
        custom_stock: form.value.custom_stock,
      })
    } else {
      const productId = form.value.product ?? editing.value?.product_id
      const blisterId = form.value.blister ?? editing.value?.blister_id
      if (!productId || !blisterId) {
        toast.error('Falta producto o presentación del catálogo')
        return
      }
      payload.source = 'catalog'
      payload.product = productId
      payload.blister = blisterId
    }

    if (editing.value) {
      await updateIngresoCatalogItem(editing.value.id, payload)
      toast.success('Ítem actualizado')
    } else {
      await createIngresoCatalogItem(payload)
      toast.success('Publicado en la tienda ingreso')
    }
    showModal.value = false
    await loadItems()
  } catch (e) {
    toast.error(e.response?.data?.detail || e.response?.data?.custom_name?.[0] || 'Error al guardar')
  } finally {
    saving.value = false
  }
}

const runImport = async () => {
  importing.value = true
  try {
    const entries = selectedImport.value.map((key) => {
      const row = importRows.value.find((r) => r.key === key)
      return { product: row.product, blister: row.blister }
    })
    const result = await bulkImportIngresoCatalog({
      entries,
      ...importDefaults.value,
      is_active: true,
    })
    toast.success(
      `${result.created_count} importados${result.skipped_count ? `, ${result.skipped_count} omitidos` : ''}`
    )
    showImport.value = false
    await loadItems()
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al importar')
  } finally {
    importing.value = false
  }
}

const deleteItems = async (ids) => {
  for (const id of ids) {
    await deleteIngresoCatalogItem(id)
  }
}

const requestSingleDelete = (item) => {
  itemToDelete.value = item
  openConfirm({
    title: 'Quitar de la tienda ingreso',
    message: `Se quitará "${item.product_name}" (x${item.seed_count}) de ingreso.kameseeds.com.`,
    detail: 'El producto dejará de estar disponible para compra en la tienda interna.',
    confirmLabel: 'Quitar',
    confirmClass: 'btn-danger',
    handler: executeSingleDelete,
  })
}

const requestBulkDelete = () => {
  const count = selectedCount.value
  if (!count) return
  openConfirm({
    title: 'Quitar seleccionados',
    message: `Se quitarán ${count} producto(s) de la tienda ingreso.`,
    detail: 'Dejarán de estar disponibles para compra en ingreso.kameseeds.com.',
    confirmLabel: 'Quitar de tienda',
    confirmClass: 'btn-danger',
    handler: executeBulkDelete,
  })
}

const requestBulkActive = (active) => {
  const count = selectedCount.value
  if (!count) return
  openConfirm({
    title: active ? 'Activar en tienda' : 'Desactivar en tienda',
    message: active
      ? `Se activarán ${count} producto(s) en ingreso.kameseeds.com.`
      : `Se desactivarán ${count} producto(s) en la tienda ingreso.`,
    detail: active
      ? 'Los clientes podrán ver y comprar estos productos.'
      : 'Los productos permanecen en el panel pero no se muestran en la tienda.',
    confirmLabel: active ? 'Activar' : 'Desactivar',
    confirmClass: active ? 'btn-success' : 'btn-secondary',
    handler: () => executeBulkActive(active),
  })
}

const executeSingleDelete = async () => {
  if (!itemToDelete.value) return
  await deleteIngresoCatalogItem(itemToDelete.value.id)
  toast.success('Eliminado')
  await loadItems()
}

const executeBulkDelete = async () => {
  bulkWorking.value = true
  try {
    const ids = [...selectedIds.value]
    await deleteItems(ids)
    toast.success(`${ids.length} producto(s) eliminados`)
    clear()
    await loadItems()
  } finally {
    bulkWorking.value = false
  }
}

const executeBulkActive = async (active) => {
  bulkWorking.value = true
  try {
    const ids = [...selectedIds.value]
    for (const id of ids) {
      await updateIngresoCatalogItem(id, { is_active: active })
    }
    toast.success(`${ids.length} producto(s) ${active ? 'activados' : 'desactivados'}`)
    clear()
    await loadItems()
  } finally {
    bulkWorking.value = false
  }
}

let stopProductChanges
let pendingProductRefresh = false
let refreshingProducts = false
let disposed = false
const refreshChangedProducts = async () => {
  if (!pendingProductRefresh || disposed || refreshingProducts || loading.value || saving.value ||
      importing.value || bulkWorking.value || showModal.value || showImport.value) return
  pendingProductRefresh = false
  refreshingProducts = true
  try { await Promise.all([loadItems(), loadProducts()]) }
  catch (error) { toast.error(apiErrorMessage(error, 'No se pudo actualizar la tienda.')) }
  finally {
    refreshingProducts = false
    if (pendingProductRefresh) refreshChangedProducts()
  }
}
watch([loading, saving, importing, bulkWorking, showModal, showImport], refreshChangedProducts)
onBeforeUnmount(() => {
  disposed = true
  itemsSeq += 1
  productsSeq += 1
  stopProductChanges?.()
})
onMounted(async () => {
  stopProductChanges = onProductsChanged(() => {
    pendingProductRefresh = true
    refreshChangedProducts()
  })
  loading.value = true
  try {
    const [, , shipCfg] = await Promise.all([
      loadItems(),
      loadProducts(),
      getShippingAdminConfig().catch(() => null),
    ])
    if (shipCfg) {
      importDefaults.value = {
        weight_grams: Number(shipCfg.default_weight) || 500,
        length_cm: Number(shipCfg.default_length) || 20,
        width_cm: Number(shipCfg.default_width) || 15,
        height_cm: Number(shipCfg.default_height) || 10,
      }
    }
  } catch (error) {
    toast.error(apiErrorMessage(error, 'No se pudo cargar la tienda ingreso.'))
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.modal-lg {
  max-width: 720px;
  width: 100%;
}

.catalog-edit-summary {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.product-card {
  border: 1px solid var(--panel-border);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.product-card:hover {
  transform: translateY(-0.2rem);
}

.product-card--selected {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px rgba(var(--bs-primary-rgb), 0.35);
}

.product-thumb-wrap {
  width: 100%;
  max-width: 160px;
}

.product-thumb {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 0.5rem;
}

.product-thumb-placeholder,
.list-thumb--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.35);
}

.product-thumb-placeholder {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 0.5rem;
  font-size: 2rem;
}

.list-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 0.35rem;
}

.list-thumb--placeholder {
  width: 56px;
  height: 56px;
  border-radius: 0.35rem;
  font-size: 1.25rem;
}

.modal-thumb {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 0.35rem;
  flex-shrink: 0;
}

.import-thumb {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 0.25rem;
}
</style>
